import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada, Rotulo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, mensagemDeErro, patch, post, del, getTodos } from '../../nucleo/api';
import { dinheiro, email } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useEquipes } from './FormOportunidade';
import type { Equipe } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';

interface Membro {
  id: string;
  nome: string;
  email: string;
}

/** Caixas de seleção dos membros ativos do workspace. */
function SeletorMembros({ valor, aoMudar }: { valor: string[]; aoMudar: (v: string[]) => void }) {
  const { t } = useTranslation('pipeline');
  const q = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<Membro>('/membros') });
  return (
    <Rotulo id="equipe-membros" rotulo={t('campos.membros')}>
      <Estado consulta={q}>
        {(d) => (
          <ul id="equipe-membros" className="max-h-56 divide-y divide-borda overflow-y-auto rounded-lg border border-borda bg-sup-2">
            {d.itens.map((m) => (
              <li key={m.id}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={valor.includes(m.id)}
                    onChange={(e) => aoMudar(e.target.checked ? [...valor, m.id] : valor.filter((x) => x !== m.id))}
                    className="size-4 accent-acento"
                  />
                  <span className="min-w-0 truncate" title={m.nome}>{m.nome}</span>
                  <span className="min-w-0 truncate text-xs text-tinta-3" title={email(m.email)}>{email(m.email)}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </Estado>
    </Rotulo>
  );
}

function ModalEquipe({ atual, aoFechar, aoSalvar }: { atual: Equipe | null; aoFechar: () => void; aoSalvar: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [nome, setNome] = useState(atual?.nome ?? '');
  const [membros, setMembros] = useState<string[]>(atual?.membros.map((m) => m.id) ?? []);
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);
  const salvar = useMutation({
    mutationFn: () => (atual ? patch(`/pipeline/equipes/${atual.id}`, { nome, membros }) : post('/pipeline/equipes', { nome, membros })),
    onSuccess: () => {
      toast.sucesso(tn('comum.salvo'));
      aoSalvar();
    },
    onError: (e) => setErro(e instanceof ErroRequisicao ? e.porCampo().nome ?? mensagemDeErro(e) : mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('modal.equipeTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao
            variante="primario"
            carregando={salvar.isPending}
            onClick={() => {
              if (!nome.trim()) return setErro(tn('erros.obrigatorio'));
              setErro(null);
              salvar.mutate();
            }}
          >
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Rotulo id="equipe-nome" rotulo={t('campos.nome')} obrigatorio erro={erro ?? undefined}>
          <Entrada id="equipe-nome" value={nome} maxLength={80} aria-invalid={erro ? true : undefined} aria-describedby={erro ? 'equipe-nome-erro' : undefined} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <SeletorMembros valor={membros} aoMudar={setMembros} />
      </div>
    </Modal>
  );
}

/** M2.4 aba Equipes: equipes de vendas, membros e o valor em negociação de cada uma. */
// Val5 (29/10): metas e equipes têm ação própria (`pipeline.metas`).
export function Equipes() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useEquipes();
  const [editando, setEditando] = useState<Equipe | 'nova' | null>(null);
  const [excluindo, setExcluindo] = useState<Equipe | null>(null);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline'] });
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/pipeline/equipes/${id}`),
    onSuccess: async () => {
      setExcluindo(null);
      toast.sucesso(tn('comum.removido'));
      await recarregar();
    },
    onError: (e) => {
      setExcluindo(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  return (
    <>
      {s.pode('pipeline.metas') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setEditando('nova')}>
            {t('acoes.novaEquipe')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Cartao semPadding>
              <Vazio texto={t('equipes.vazio')} />
            </Cartao>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {d.itens.map((e) => (
                <Cartao
                  key={e.id}
                  titulo={e.nome}
                  acao={
                    <span className="flex items-center gap-1">
                      {s.pode('pipeline.metas') && <Botao tamanho="sm" variante="fantasma" aria-label={tn('comum.editar')} onClick={() => setEditando(e)} icone={<Pencil className="size-4" />} />}
                      {s.pode('pipeline.metas') && <Botao tamanho="sm" variante="fantasma" aria-label={tn('comum.excluir')} onClick={() => setExcluindo(e)} icone={<Trash2 className="size-4" />} />}
                    </span>
                  }
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap gap-1.5">
                      {e.membros.length === 0 ? <span className="text-sm text-tinta-3">{t('equipes.semMembros')}</span> : e.membros.map((m) => <Selo key={m.id}>{m.nome}</Selo>)}
                    </div>
                    <p className="text-sm text-tinta-2">
                      <span className="numero">{t('equipes.abertas', { count: e.abertas })}</span>
                      {'. '}
                      <span className="numero">{t('equipes.valor', { valor: dinheiro(e.valor_aberto_centavos) })}</span>
                    </p>
                  </div>
                </Cartao>
              ))}
            </div>
          )
        }
      </Estado>
      {editando && (
        <ModalEquipe
          key={editando === 'nova' ? 'nova' : editando.id}
          atual={editando === 'nova' ? null : editando}
          aoFechar={() => setEditando(null)}
          aoSalvar={async () => {
            setEditando(null);
            await recarregar();
          }}
        />
      )}
      <Confirmar
        aberto={!!excluindo}
        aoFechar={() => setExcluindo(null)}
        aoConfirmar={() => excluindo && excluir.mutate(excluindo.id)}
        titulo={t('confirmar.excluir')}
        texto={t('confirmar.excluirEquipe')}
        perigoso
        carregando={excluir.isPending}
      />
    </>
  );
}
